import { all, call, put, race, select, take, takeEvery } from 'redux-saga/effects'
import _ from 'lodash'
import ls from 'local-storage'
import log from 'loglevel'
import { SUCCESS, FAILED } from 'crom-api-constants'

import { error } from '../toastr'

import config from '../../config'

import {
    sessionKey,
    LOGIN,
    LOGIN_REQUEST,
    LOGOUT,
    LOGOUT_REQUEST,
    USER_INFO_REQUEST,
    AVAILABLE_MODULES_REQUEST,
    LOCALE_PREFIX,
} from './constants'

import {
    loginRequest,
    logoutRequest,
    availableModulesRequest,
    setLoading,
    setAuthorize,
    userInfoRequest,
} from './actions'
import { setActiveModule } from '../routing/actions'
import { setLocale } from '../translations/actions'
// iframeSolution
import { openReportById } from '../../scenes/Gnosus/scenes/Report/actions'
import { openPortfolioById } from '../../scenes/Gnosus/scenes/Portfolio/actions'

import { isAuthorizedOrLoading } from './selectors'
import { getAppState, postAppState } from '../appState/actions'
import { GET_APP_STATE_REQUEST, POST_APP_STATE_REQUEST } from '../appState/constants'

// compound action types
const LOGIN_REQUEST_SUCCESS = `${LOGIN_REQUEST}_${SUCCESS}`
const LOGIN_REQUEST_FAILED = `${LOGIN_REQUEST}_${FAILED}`
const LOGOUT_REQUEST_SUCCESS = `${LOGOUT_REQUEST}_${SUCCESS}`
const LOGOUT_REQUEST_FAILED = `${LOGOUT_REQUEST}_${FAILED}`
const USER_INFO_REQUEST_SUCCESS = `${USER_INFO_REQUEST}_${SUCCESS}`
const USER_INFO_REQUEST_FAILED = `${USER_INFO_REQUEST}_${FAILED}`
const AVAILABLE_MODULES_REQUEST_SUCCESS = `${AVAILABLE_MODULES_REQUEST}_${SUCCESS}`
const AVAILABLE_MODULES_REQUEST_FAILED = `${AVAILABLE_MODULES_REQUEST}_${FAILED}`

const failedRegex = new RegExp(`^.*_${FAILED}$`)

export function isFailedAction(_action) {
    return _action.type && failedRegex.test(_action.type)
}

export function* loginProcessCallSaga(_action) {
    const username = _.get(_action, 'data.username', null)
    const password = _.get(_action, 'data.password', null)
    const redirectModule = _.get(_action, 'data.redirectModule', null)
    const id = _.get(_action, 'data.id', null)
    const language = _.get(_action, 'data.language', null)
    const autologin = _.get(_action, 'data.autologin', false)
    let sessionId = _.get(_action, 'data.sessionId', null)

    // if user is authorized or login in process => stop login saga
    const authorizedOrLoading = yield select(isAuthorizedOrLoading)
    if (authorizedOrLoading) return null

    yield put(setLoading(true))

    // if no autologin => try login with username and password
    if (!autologin) {
        const loginResult = yield all({
            response: race({
                success: take(LOGIN_REQUEST_SUCCESS),
                failed: take(LOGIN_REQUEST_FAILED),
            }),
            request: put(loginRequest(username, password)),
        })

        // failed => failedRequestCallSaga
        if (loginResult.response.failed) {
            const loginError = _.get(loginResult, 'response.failed.payload.error', null)

            if (loginError.code === 401) {
                const errorType = yield loginError.res.text()

                error(
                    {
                        id: `${LOCALE_PREFIX}.error.login.401.${errorType}.text`,
                    },
                    {
                        id: `${LOCALE_PREFIX}.error.login.401.${errorType}.title`,
                    },
                    {
                        timeOut: 2000,
                        removeOnHover: true,
                    }
                )
            }

            yield put(setLoading(false))

            return null
        }

        sessionId = _.get(loginResult.response.success, 'payload.response.body.sessionId', null)
    }

    if (sessionId === null) {
        yield put(setLoading(false))
        return null
    }

    // load data about user (save in reducer)
    const userInfoResult = yield all({
        response: race({
            success: take(USER_INFO_REQUEST_SUCCESS),
            failed: take(USER_INFO_REQUEST_FAILED),
        }),
        request: put(userInfoRequest(sessionId)),
    })

    // failed => failedRequestCallSaga
    if (userInfoResult.response.failed) {
        yield put(setLoading(false))
        return null
    }

    // load app-state - see services/appState saga (wait for response even if the request fails)
    yield all([put(getAppState()), take(`${GET_APP_STATE_REQUEST}_COMPLETE`)])

    // load available modules (save in reducer)
    const availableModulesResult = yield all({
        response: race({
            success: take(AVAILABLE_MODULES_REQUEST_SUCCESS),
            failed: take(AVAILABLE_MODULES_REQUEST_FAILED),
        }),
        request: put(availableModulesRequest(sessionId)),
    })

    // failed => failedRequestCallSaga
    if (availableModulesResult.response.failed) {
        yield put(setLoading(false))
        return null
    }

    yield put(setAuthorize(true))
    yield put(setLoading(false))

    // save session id to local storage
    yield call(ls, sessionKey, sessionId)

    // if language => set locale to language
    if (language) {
        yield put(setLocale(language))
    }

    // if module for redirect => do redirect :)
    if (redirectModule) {
        yield put(setActiveModule(redirectModule))

        // open dynamic link from autologin (report and portfolio for now)
        if (config.iframeSolution && id) {
            if (redirectModule === config.modules.report.key) yield put(openReportById(_.toString(id)))
            if (redirectModule === config.modules.portfolio.key) yield put(openPortfolioById(_.toString(id)))
        }
    }

    return null
}

export function* logoutProcessCallSaga() {
    yield call(ls.remove, sessionKey)

    // post app-state and wait for response (even if request fails)
    yield all([put(postAppState()), take(`${POST_APP_STATE_REQUEST}_COMPLETE`)])

    yield put(setLoading(false))

    const logoutResult = yield all({
        response: race({
            success: take(LOGOUT_REQUEST_SUCCESS),
            failed: take(LOGOUT_REQUEST_FAILED),
        }),
        request: put(logoutRequest()),
    })

    if (logoutResult.response.failed) {
        log.warn('Something went wrong with logout')
    }

    // TODO confirm dialog for better user experience
    window.location.href = config.history.basename

    return null
}

export function* failedRequestCallSaga(_action) {
    const errorCode = _.get(_action, 'payload.error.code', null)
    const actionType = _.get(_action, 'type', null)

    if (errorCode === 401) {
        // when login failed with 401 => just set loading to false and nothing else !!!
        if (actionType !== LOGIN_REQUEST_FAILED) yield call(logoutProcessCallSaga)
        else yield put(setLoading(false))
    }

    return null
}

// all authorization sagas
export default function* authorizationSaga() {
    yield all([
        takeEvery(LOGIN, loginProcessCallSaga),
        takeEvery(LOGOUT, logoutProcessCallSaga),
        takeEvery(isFailedAction, failedRequestCallSaga),
    ])
}
